import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
  computed,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { IconComponent } from '@ui/icon/icon.component';
import { InputComponent } from '@ui/input/input.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { SelectComponent } from '@ui/select/select.component';
import { StepperComponent } from '@ui/stepper/stepper.component';
import { FileUploadComponent } from '@ui/file-upload/file-upload.component';
import { RequestsService } from '@api/services/requests.service';
import { Request, RequestDetail } from '@api/models/request.model';
import { forkJoin } from 'rxjs';
import { LoadingService } from '@ui/app-layout/loading-service';
import { debounceTime, filter, tap, switchMap } from 'rxjs/operators';
import { RequestDetailsStepComponent } from './request-details-step/request-details-step.component';
import { RequestWmoReferenceStepComponent } from './request-wmo-reference-step/request-wmo-reference-step.component';
import { RequestIntroStepComponent } from './request-intro-step/request-intro-step.component';
import { RequestWithdrawalSearchStepComponent } from './request-withdrawal-search-step/request-withdrawal-search-step.component';

// Add interface for Step
interface Step {
  label: string;
  value: number;
}

@Component({
  selector: 'app-create-request-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    InputComponent,
    ReactiveFormsModule,
    StepperComponent,
    IconComponent,
    FileUploadComponent,
    SelectComponent,
    RequestDetailsStepComponent,
    RequestWmoReferenceStepComponent,
    RequestIntroStepComponent,
    RequestWithdrawalSearchStepComponent,
  ],
  template: `
    <ui-modal
      [title]="getModalTitle()"
      [submitText]="
        currentStep() === steps().length - 1 || steps().length === 1
          ? 'Opslaan'
          : 'Volgende'
      "
      [cancelText]="'Annuleren'"
      [disabled]="isCheckingWmoReference()"
      (close)="closeModal()"
      (save)="nextStep()"
      [size]="'xl'"
      [buttonsLeft]="[
        {
          text: 'Vorige',
          variant: 'ghost',
          disabled: currentStep() < 1,
          icon: 'chevron-left',
          onClick: this.previousStep
        }
      ]"
    >
      @if(steps().length > 1 && currentStep() > 0) {
      <ui-stepper [steps]="steps()" [currentStep]="currentStep()"></ui-stepper>
      }

      @if(errorMessage()) {
      <div
        class="mt-4 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-800"
        role="alert"
      >
        <p class="font-medium">{{ errorTitle() }}</p>
        <p class="mt-1">{{ errorMessage() }}</p>
        @if(errorHint()) {
        <p class="mt-2 text-danger-700">{{ errorHint() }}</p>
        }
      </div>
      }

      <form [formGroup]="requestForm" class="mt-4 mb-4">
        <!-- Introductie stap -->
        @if(currentStep() === 0 && !existingRequest) {
        <app-request-intro-step
          [selectedType]="getIntroSelectedType()"
          (typeSelected)="onTypeSelected($event)"
        ></app-request-intro-step>
        }

        <!-- WMO referentie stap -->
        @if(currentStep() === 1 && type() === 'request') {
        <app-request-wmo-reference-step
          [requestForm]="requestForm"
          [isEditMode]="isEditMode()"
          [importedClientWmoRef]="importedClientWmoRef()"
          (importCommitted)="onWmoImportCommitted($event)"
          (importCleared)="onWmoImportCleared()"
        ></app-request-wmo-reference-step>
        }

        <!-- Terugname zoek stap -->
        @if(currentStep() === 1 && type() === 'withdrawal') {
        <app-request-withdrawal-search-step
          [requestForm]="requestForm"
        ></app-request-withdrawal-search-step>
        }

        <!-- Aanvraag gegevens stap -->
        @if(currentStep() === 2 || (currentStep() === 0 && existingRequest)) {
        <app-request-details-step
          [requestForm]="requestForm"
          [isReadOnly]="isReadOnly"
        ></app-request-details-step>
        }

        <!-- Documenten stap (alleen bij nieuwe aanvraag) -->
        <div
          *ngIf="currentStep() === 3 && !existingRequest"
          formGroupName="documents"
          class="space-y-6"
        >
          <div class="space-y-4">
            <h2 class="text-lg font-medium flex items-center gap-2">
              <ui-icon name="document-text" class="w-5 h-5"></ui-icon>
              Document upload
            </h2>
            <div class="flex flex-col gap-4">
              <ui-file-upload
                formControlName="files"
                [required]="false"
                [multiple]="true"
                accept=".pdf,.doc,.docx"
                helperText="Toegestane bestandstypen: PDF, DOC, DOCX (max 10MB)"
              ></ui-file-upload>
            </div>
          </div>
        </div>
      </form>
    </ui-modal>
  `,
  styles: `
    :host {
      display: block;
    }

    .steps-indicator {
      .step-item {
        @apply px-4 py-2 rounded-md;
        &.active {
          @apply bg-primary-500 text-white;
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateRequestModalComponent {
  private fb = inject(FormBuilder);
  private requestsService = inject(RequestsService);
  private modalService = inject(ModalService);
  private modalRef = inject(MODAL_REF);
  private loadingService = inject(LoadingService);

  wmoReferenceError = signal<string | undefined>(undefined);
  isCheckingWmoReference = signal<boolean>(false);
  existingRequestDetails = signal<any | null>(null);
  useExistingReference = signal<boolean>(false);
  type = signal<'request' | 'withdrawal'>('request');
  @Input() existingRequest: RequestDetail | null = null;
  @Input() isReadOnly: boolean = false;
  /** Vanuit context (bijv. terugname-lijst of opdrachten): sla keuzestap over en start op stap 1. */
  @Input() initialRequestType?: 'request' | 'withdrawal';

  errorMessage = signal<string | undefined>(undefined);
  errorTitle = signal('Opslaan van de aanvraag is mislukt');
  errorHint = signal<string | undefined>(undefined);

  // Add maxDate property
  maxDate = new Date().toISOString().split('T')[0];

  steps = signal<Step[]>([]);
  currentStep = signal<number>(0);

  /**
   * WMO (getrimd) waarvoor clientgegevens via "Gebruik deze referentie" zijn ingeladen.
   * Blijft op de modal staan bij stapwissel zodat we bij wijziging van de referentie kunnen resetten.
   */
  importedClientWmoRef = signal<string | null>(null);

  onWmoImportCommitted(ref: string): void {
    const t = ref.trim();
    this.importedClientWmoRef.set(t.length ? t : null);
  }

  onWmoImportCleared(): void {
    this.importedClientWmoRef.set(null);
  }

  private postalCodeValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    // Nederlandse postcode format: 4 cijfers + optionele spatie + 2 hoofdletters (1234 AB of 1234AB)
    const postalCodeRegex = /^[1-9][0-9]{3}\s?[A-Z]{2}$/i;
    const isValid = postalCodeRegex.test(value);

    return isValid ? null : { invalidPostalCode: true };
  }

  private phoneNumberValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    // Dutch phone number format: +31 6 12345678 or 06 12345678 or 0612345678
    const phoneRegex = /^(\+31|0)[1-9][0-9]{8}$/;
    const normalizedValue = value.replace(/\s+/g, '');
    const isValid = phoneRegex.test(normalizedValue);

    return isValid ? null : { invalidPhoneNumber: true };
  }

  private bsnValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    // BSN must be exactly 9 digits
    const bsnRegex = /^\d{9}$/;
    const normalizedValue = value.replace(/\s+/g, '');
    const isValid = bsnRegex.test(normalizedValue);

    return isValid ? null : { invalidBsn: true };
  }

  requestForm = this.fb.group({
    type: ['', Validators.required],
    wmo_reference: this.fb.group({
      wmo_reference: ['', Validators.required],
    }),
    wmo_so_reference: [''],
    factuurreferentie: [''],
    bsn: ['', [this.bsnValidator.bind(this)]],
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    street: ['', Validators.required],
    housenumber: ['', Validators.required],
    housenumber_addon: [''],
    postal_code: [
      '',
      [Validators.required, this.postalCodeValidator.bind(this)],
    ],
    city: ['', Validators.required],
    phone: ['', [Validators.required, this.phoneNumberValidator.bind(this)]],
    email: ['', [Validators.email]],
    description: [''],
    remarks: [''],
    contact_id: [''],
    date_of_birth: [
      '',
      [
        Validators.required,
        (control: AbstractControl) => {
          if (!control.value) return null;
          const date = new Date(control.value);
          const today = new Date();
          if (date > today) {
            return { futureDate: true };
          }
          return null;
        },
      ],
    ],
    ...(!this.existingRequest
      ? {
          documents: this.fb.group({
            files: [[] as File[]],
          }),
        }
      : {}),
  });

  isEditMode = computed(() => !!this.existingRequest);

  getModalTitle(): string {
    if (this.existingRequest) {
      return 'Aanvraag aanpassen';
    }
    if (this.initialRequestType === 'withdrawal') {
      return 'Nieuwe terugname aanvraag';
    }
    return 'Nieuwe aanvraag';
  }

  /** Alleen bij stap 0: toon welke keuze al in het formulier staat (bijv. na “Vorige”). */
  getIntroSelectedType(): 'request' | 'withdrawal' | null {
    if (this.existingRequest || this.currentStep() !== 0) {
      return null;
    }
    const v = this.requestForm.get('type')?.value;
    return v === 'request' || v === 'withdrawal' ? v : null;
  }

  ngOnInit() {
    if (this.existingRequest) {
      this.requestForm.patchValue({
        type: this.existingRequest.type || 'request',
        ...this.existingRequest.details,
        remarks: this.existingRequest.remarks || '',
      });

      this.requestForm.get('wmo_reference')?.get('wmo_reference')?.setValue(this.existingRequest.wmo_reference);

      this.steps.set([
        { label: 'Aanvraag gegevens', value: 0 },
      ]);
    } else {
      this.steps.set([
        { label: 'Type aanvraag', value: 0 },
        { label: 'WMO referentie', value: 1 },
        { label: 'Client gegevens', value: 2 },
        { label: 'Documenten', value: 3 },
      ]);
      const preset = this.initialRequestType;
      if (preset === 'request' || preset === 'withdrawal') {
        this.type.set(preset);
        this.requestForm.patchValue({ type: preset });
        this.updateSteps(preset);
        this.currentStep.set(1);
      }
    }
  }

  onTypeSelected(type: 'request' | 'withdrawal') {
    this.importedClientWmoRef.set(null);
    this.type.set(type);
    this.requestForm.patchValue({ type });
    this.updateSteps(type);
    this.currentStep.update((step) => step + 1);
  }

  private updateSteps(type: 'request' | 'withdrawal') {
    if (type === 'request') {
      this.steps.set([
        { label: 'Type aanvraag', value: 0 },
        { label: 'WMO referentie', value: 1 },
        { label: 'Client gegevens', value: 2 },
        { label: 'Documenten', value: 3 },
      ]);
    } else {
      this.steps.set([
        { label: 'Type aanvraag', value: 0 },
        { label: 'Zoek bestaande lift', value: 1 },
        { label: 'Client gegevens', value: 2 },
        { label: 'Documenten', value: 3 },
      ]);
    }
  }

  nextStep() {
    if (
      this.currentStep() === this.steps().length - 1 ||
      this.steps().length === 1
    ) {
      this.saveModal();
      return;
    }

    this.clearError();
    const currentFormGroup = this.getCurrentFormGroup();
    currentFormGroup.markAllAsTouched();

    if (
      this.type() === 'withdrawal' &&
      this.currentStep() === 1 &&
      this.requestForm.get('postal_code')?.valid &&
      this.requestForm.get('housenumber')?.valid &&
      this.requestForm.get('wmo_reference')?.get('wmo_reference')?.valid
    ) {
      this.currentStep.update((step) => step + 1);
    } else if (currentFormGroup.valid) {
      this.currentStep.update((step) => step + 1);
    }
  }

  private clearError(): void {
    this.errorMessage.set(undefined);
    this.errorHint.set(undefined);
    this.errorTitle.set('Opslaan van de aanvraag is mislukt');
  }

  private setSaveError(message: string): void {
    this.errorTitle.set('Opslaan van de aanvraag is mislukt');
    this.errorMessage.set(message);
    this.errorHint.set(
      'Controleer of alle stappen correct zijn ingevuld. Neem contact op met support als het probleem aanhoudt.'
    );
  }

  private setUploadError(message: string): void {
    this.errorTitle.set('Document upload mislukt');
    this.errorMessage.set(message);
    this.errorHint.set(
      'De aanvraag zelf is wel opgeslagen. Probeer het document opnieuw te uploaden via de aanvraag.'
    );
  }

  previousStep = () => {
    if (this.currentStep() > 0) {
      this.clearError();
      this.currentStep.update((step) => step - 1);
    }
  };

  checkExistingContact = () => {
    // this.requestsService.checkExistingContact(this.requestForm.value).subscribe({
    //   next: (response) => {
    //     console.log('response', response);
    //   },
    // });
  };

  private getCurrentFormGroup(): FormGroup {
    if (this.currentStep() === 1) {
      return this.requestForm.get('wmo_reference') as FormGroup;
    } else if (this.currentStep() === 2) {
      return this.requestForm;
    } else if (this.currentStep() === 3) {
      return this.requestForm.get('documents') as FormGroup;
    }
    return this.requestForm;
  }

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<any>);
  }

  saveModal() {
    this.requestForm.markAllAsTouched();
    if (this.requestForm.valid) {
      this.clearError();
      this.loadingService.show();

      // Gebruik getRawValue() om ook uitgeschakelde velden mee te nemen
      const formData = this.requestForm.getRawValue();
      const request = formData as any;
      request.wmo_reference =
        this.requestForm.get('wmo_reference')?.get('wmo_reference')?.value ||
        '';
      request.so_referentie =
        this.requestForm.get('wmo_reference')?.get('so_referentie')?.value ||
        '';
      request.factuurreferentie =
        this.requestForm.get('factuurreferentie')?.value || '';
      const files = this.requestForm.value.documents?.files;

      // Bij update zorgen dat alle Zoho IDs worden meegestuurd
      if (this.existingRequest) {
        // Overnemen van alle Zoho IDs
        request.zoho_lead_id = this.existingRequest.zoho_lead_id;
        request.zoho_contact_id = this.existingRequest.zoho_contact_id;
        request.zoho_task_id = this.existingRequest.zoho_task_id;
        request.zoho_verkoopkans_id = this.existingRequest.zoho_verkoopkans_id;
      }

      // Create or update the request first
      const requestOperation = this.existingRequest
        ? this.requestsService.updateRequest(this.existingRequest.id, request)
        : this.requestsService.createRequest(request);

      requestOperation.subscribe({
        next: (savedRequest) => {
          console.log('savedRequest', savedRequest);
          // After request is saved, upload any files if present
          if (files && files.length > 0) {
            // Create an array of upload operations
            const uploadOperations = files.map((file) =>
              this.requestsService.createRequestAttachment(
                savedRequest.id,
                file
              )
            );

            // Execute all uploads in parallel
            forkJoin(uploadOperations).subscribe({
              next: () => {
                this.loadingService.hide();
                this.modalService.close(this.modalRef as ModalRef<any>);
              },
              error: (error) => {
                console.error('Error uploading files:', error);
                this.setUploadError(
                  error.error?.message ||
                    'Het uploaden van documenten is mislukt.'
                );
                this.loadingService.hide();
              },
            });
          } else {
            this.loadingService.hide();
            this.modalService.close(this.modalRef as ModalRef<any>);
          }
        },
        error: (error) => {
          console.error('Error saving request:', error);
          this.setSaveError(
            error.error?.message ||
              'Er is een onbekende fout opgetreden bij het opslaan van de aanvraag.'
          );
          this.loadingService.hide();
        },
      });
    }
  }
}
